import { getLocalStorage, removeLocalStorage, setLocalStorage } from '@/utils/common/storage'
import { goPage } from '@/utils/browser/page'

import type {
  LoginReqType,
  LoginDataType,
  SignUpReqType,
  ForgotPasswordReqType
} from '@/models/types/auth.types'
import { useI18n } from 'vue-i18n'
import { useMessage } from '@/composables/useMessage'
import { useNotification } from '@/composables/useNotification'
import { useGoogleAuth } from '@/composables/useGoogleAuth'
import { api } from '@/services'
import { useUserStore } from '@/stores/user.stores'
import { queryClient } from '@/plugins/vue-query'
import { catchErrorHandler } from '@/utils/api/error-handler'
import { STORE_INFO, REMEMBER_ME } from '@/constants/storageKey.const'

/** 處理登入、註冊相關 */
export const useAuth = () => {
  const { t: $t } = useI18n()
  const { openMessage } = useMessage()
  const { openNotification } = useNotification()
  const { initializeThirdPartyStorage } = useGoogleAuth()
  const userStore = useUserStore()

  /**
   * 存儲「記住我」資訊。
   * @remarks 只存 userId，不存密碼——密碼一律交給瀏覽器原生的帳號密碼管理器
   *          （login input 的 autocomplete="username"/"current-password"）記住。
   *          `setLocalStorage` 的第三個參數只是「存 localStorage 而非 sessionStorage」
   *          的選擇，不是加密，這裡存的 userId 本來就不是需要保密的憑證（OP#2604：
   *          舊版曾經連明文密碼一起存進來，是可撤銷性最差的憑證形式，任何 XSS 都能
   *          偷到永久有效的密碼；新版只存帳號，就算外洩也不到「憑證外洩」的程度）。
   */
  const _saveLoginInfo = (userId: string): void => {
    setLocalStorage(REMEMBER_ME, { userId }, true)
  }

  /**
   * 讀取記住我資訊（password 一律回傳空字串，實際密碼交由瀏覽器 autocomplete 帶入）。
   * @remarks 自我修復舊版遺留資料：舊版會把明文密碼一起存進 `REMEMBER_ME`，這裡讀到
   *          帶有 `password` 欄位的舊格式時，立刻改寫成只含 userId 的新格式，讓已經
   *          用過「記住我」的使用者，密碼也能在下次打開登入頁時被清掉，不用等他們
   *          重新登入一次或手動清 localStorage（OP#2604）。
   */
  const loadLoginInfo = (): LoginDataType | null => {
    const rememberMe = getLocalStorage<{ userId: string; password?: string }>(REMEMBER_ME, true)
    if (!rememberMe) return null

    if ('password' in rememberMe) {
      _saveLoginInfo(rememberMe.userId)
    }

    return {
      userId: rememberMe.userId,
      password: '',
      rememberMe: true
    }
  }

  /**
   * 執行帳號密碼登入流程。
   * @param params - 登入憑證（userId / password）
   * @param isRememberMe - 是否記住登入資訊
   * @returns 登入成功回傳 `true`，失敗（含 API 錯誤與業務層錯誤）回傳 `false`
   * @remarks 成功後會將後端回傳的 JWT token 寫入 `userStore.token`，
   *          由 axios-instance 的攔截器在後續請求自動帶入。
   */
  const fnLogin = async (params: LoginReqType, isRememberMe: boolean): Promise<boolean> => {
    try {
      // 取消勾選一律先清掉既有記錄，不受這次登入成功與否影響——使用者取消記住我的
      // 意願跟這次密碼打對打錯無關；新增記住我的動作則要等登入成功才做（見下方），
      // 避免把尚未驗證（甚至錯誤）的帳號存進 localStorage
      if (!isRememberMe) removeLocalStorage(REMEMBER_ME, true)

      const { result, isSuccess, resultCode } = await api.auth.login(params)

      if (!isSuccess) {
        if (!resultCode) {
          return false
        }

        openNotification(
          {
            title: $t('Common.Response.Error'),
            subTitle: $t(`ServerResponse.ResultCode.${resultCode}`)
          },
          'error'
        )
        return false
      }

      // token 寫進 userStore 後，後續請求會自動帶入 Authorization；先設定 token 再處理
      // 記住我，萬一 result 格式異常在這裡拋出，記住我也不會被誤存
      // （isSuccess 為 true 時 result 必定存在，是後端對這支 API 的既有約定）
      userStore.token = result!.token
      // OP#2664：密碼超過 180 天未更換，交給 usePasswordExpiryPrompt 監看並彈出提示
      userStore.isPwdNotChangeOver180Days = result!.isPwdNotChangeOver180Days

      if (isRememberMe) _saveLoginInfo(params.userId)

      return true
    } catch (e) {
      catchErrorHandler(e)
      return false
    }
  }

  /** 處理註冊 */
  const fnSignUp = async (params: SignUpReqType): Promise<boolean> => {
    try {
      const { userId, password, name } = params
      const { isSuccess, resultCode } = await api.auth.signIn({ userId, password, name })

      if (!isSuccess) {
        if (!resultCode) {
          return false
        }

        openNotification(
          {
            subTitle: $t(`ServerResponse.ResultCode.${resultCode}`)
          },
          'error',
          'top'
        )
        return false
      }

      // 註冊成功後回登入頁，重新走正式登入流程
      openNotification(
        {
          subTitle: $t('Common.Result.SignUpSuccess')
        },
        'success',
        'top',
        () => {
          goPage('/login')
        },
        {
          hasIcon: false,
          hasClose: false
        }
      )

      return true
    } catch (e) {
      catchErrorHandler(e)
      return false
    }
  }

  /** 處理註冊驗證 */
  const fnSignUpValidate = async (validateCode: string): Promise<void> => {
    try {
      const { isSuccess, resultCode } = await api.auth.validate(validateCode)

      if (!isSuccess) {
        if (!resultCode) {
          return
        }

        // 驗證失敗也回登入頁，避免卡在失效驗證碼頁
        return openNotification(
          {
            title: $t('Common.Response.Error'),
            subTitle: $t(`ServerResponse.ResultCode.${resultCode}`)
          },
          'error',
          'top',
          () => {
            goPage('/login')
          }
        )
      }

      // 驗證成功後仍回登入頁，避免在驗證頁直接建立半套登入態
      return openNotification(
        {
          title: $t('Common.Response.Success'),
          subTitle: $t('Common.Result.ValidateSuccess')
        },
        'success',
        'top',
        () => {
          goPage('/login')
        }
      )
    } catch (e) {
      catchErrorHandler(e)
    }
  }

  /**
   * 清除本機登入狀態（登出時使用）。
   * @remarks 獨立成一個函式是因為本機狀態的清除不能綁在登出 API 是否成功上——
   *          網路異常或後端出錯時，使用者在本機端仍然應該被視為已登出，不能卡在
   *          「看起來已登出、token 卻還留著」的狀態，讓下一位使用共用裝置的人
   *          有機會沿用前一位的登入態（OP#2604）。
   *
   *          不清除 REMEMBER_ME：「記住我」的目的就是下次登入免打帳號，登出是
   *          正常操作、不代表使用者想被忘記，只有在登入頁明確取消勾選「記住我」
   *          才會清掉（見 fnLogin）。REMEMBER_ME 現在只存 userId（密碼交給瀏覽器
   *          原生帳號密碼管理器），不是需要保密的憑證，繼續留著不影響資安。
   *
   *          TanStack Query 快取要主動清除，不能只靠 goPage 整頁重新整理「順便」
   *          清掉：整頁重整只是瀏覽器記憶體被回收的副作用，萬一之後改成 SPA 導頁
   *          （router.push），沒清乾淨就會讓下一位使用同一台裝置登入的人，短暫
   *          沿用前一位使用者的快取資料（OP#2615）。systemVersion store 不需要
   *          特別清：下一位登入後 fnGetUserInfo 會重新驗證並覆蓋這些旗標，殘留值
   *          不構成安全性問題。
   */
  const _clearLocalSession = (): void => {
    userStore.initLoginState()
    queryClient.clear()
    removeLocalStorage(STORE_INFO)
    // 清除 Google 等第三方登入的暫存資料（FROM_THIRD_PARTY_ID / TYPE）
    initializeThirdPartyStorage()
    // 不等提示訊息消失就直接導頁，避免登出後多等 3 秒
    goPage('/login')
  }

  /**
   * 執行登出流程。
   * @remarks 先呼叫登出 API（此時 token 仍有效，後端才能真的讓該 token 失效），
   *          不論 API 結果是成功、業務層錯誤、或直接拋出例外，本機登入狀態都一定
   *          會被清除（見 `_clearLocalSession`）。
   */
  const fnLogOut = async (): Promise<void> => {
    try {
      const { isSuccess, resultCode } = await api.auth.logout()
      _clearLocalSession()

      if (!isSuccess) {
        if (!resultCode) {
          return
        }

        return openNotification(
          {
            title: $t('Common.Response.Error'),
            subTitle: $t(`ServerResponse.ResultCode.${resultCode}`)
          },
          'error'
        )
      }

      openMessage('success', $t('Common.Result.LogoutSuccess'))
    } catch (e) {
      // _clearLocalSession 本身失敗（理論上不會，但保險起見）也不能蓋掉原本的錯誤，
      // 一定要讓 catchErrorHandler 收到 api.auth.logout() 拋出的原始例外
      try {
        _clearLocalSession()
      } catch {
        // 忽略，原始例外交給下面的 catchErrorHandler 處理
      }
      catchErrorHandler(e)
    }
  }

  /** 處理忘記密碼 */
  const fnForgotPassword = async (params: ForgotPasswordReqType): Promise<boolean> => {
    try {
      const { isSuccess, resultCode } = await api.auth.forgotPassword(params)

      if (!isSuccess) {
        if (!resultCode) {
          return false
        }

        openMessage('error', $t(`ServerResponse.ResultCode.${resultCode}`))
        return false
      }

      // 忘記密碼成功只提示寄送完成，不直接導頁
      openNotification(
        {
          title: $t('Common.Response.Success'),
          subTitle: `${$t('Common.Result.ForgotPasswordSuccess')}`
        },
        'success'
      )

      return true
    } catch (error) {
      catchErrorHandler(error)
      return false
    }
  }

  /** 處理變更密碼 */
  const fnChangePassword = async (newPwd: string) => {
    try {
      const { isSuccess, resultCode } = await api.auth.changePassword(newPwd)

      if (!isSuccess) {
        if (!resultCode) {
          return
        }

        return openMessage('error', $t(`ServerResponse.ResultCode.${resultCode}`))
      }

      // 已登入流程只提示成功，不重置登入態
      return openMessage('success', $t('Common.Result.ChangePasswordSuccess'))
    } catch (e) {
      catchErrorHandler(e)
    }
  }

  /**
   * 處理「沿用舊密碼」（OP#2664）：密碼欄位給空字串，代表使用者選擇不更換密碼，
   * 沿用既有的變更密碼 API 當作同一個操作的兩種結果。
   * @remarks 成功才清除 `isPwdNotChangeOver180Days`，失敗則保留旗標，下次仍會被提醒。
   */
  const fnKeepCurrentPassword = async (): Promise<void> => {
    try {
      const { isSuccess, resultCode } = await api.auth.changePassword('')

      if (!isSuccess) {
        if (!resultCode) {
          return
        }

        return openMessage('error', $t(`ServerResponse.ResultCode.${resultCode}`))
      }

      userStore.isPwdNotChangeOver180Days = false
      return openMessage('success', $t('Common.Result.KeepPasswordSuccess'))
    } catch (e) {
      catchErrorHandler(e)
    }
  }

  return {
    loadLoginInfo,
    fnLogin,
    fnSignUp,
    fnSignUpValidate,
    fnLogOut,
    fnForgotPassword,
    fnChangePassword,
    fnKeepCurrentPassword
  }
}
